/**
 * Creates a Google Calendar entry with an attached Google Meet conference.
 *
 * The call goes straight from the browser to the Calendar API using the access token from
 * `requestGoogleAccessToken`, so the entry lands on the signed-in organizer's own calendar.
 */

import moment from 'moment-timezone';
import { requestGoogleAccessToken, GOOGLE_CALENDAR_EVENTS_SCOPE } from './googleIdentity';

const CALENDAR_EVENTS_ENDPOINT =
  'https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1';

const defaultMeetingMinutes = 30;

export interface CreateGoogleMeetInput {
  accessToken: string;
  summary: string;
  description?: string;
  /** ISO 8601 start/end of the meeting. */
  startTime: string;
  endTime: string;
  timezone?: string;
}

export interface CreateGoogleMeetResult {
  /** The `https://meet.google.com/...` link attendees join with. */
  hangoutLink: string;
  /** Link to the entry on Google Calendar. */
  htmlLink?: string;
  calendarEventId?: string;
}

type CalendarApiResponse = {
  id?: string;
  hangoutLink?: string;
  htmlLink?: string;
  conferenceData?: {
    entryPoints?: { entryPointType?: string; uri?: string }[];
  };
  error?: { message?: string };
};

/**
 * Works out the single sitting the Meet should cover, in the event's own time zone.
 *
 * An event record's end date can fall days or months after its start — multi-day and recurring
 * events carry the date the series stops — and using it verbatim creates one enormous calendar
 * entry. A meeting always starts and ends on the same date, so only the *time of day* is taken
 * from the event's end and applied to the start's date; anything that does not yield a later
 * time (no end stored, or an end that wraps past midnight) falls back to a 30 minute meeting.
 */
export function resolveMeetingWindow(
  eventStartTime?: string | null,
  eventEndTime?: string | null,
  timezone?: string | null,
): { startTime: string; endTime: string } {
  const zone = timezone || moment.tz.guess();
  const start = eventStartTime ? moment.tz(eventStartTime, zone) : moment.tz(zone);
  const fallbackEnd = start.clone().add(defaultMeetingMinutes, 'minutes');

  if (!eventEndTime) {
    return { startTime: start.toISOString(), endTime: fallbackEnd.toISOString() };
  }

  const eventEnd = moment.tz(eventEndTime, zone);
  const sameDayEnd = start.clone().set({
    hour: eventEnd.hour(),
    minute: eventEnd.minute(),
    second: 0,
    millisecond: 0,
  });

  return {
    startTime: start.toISOString(),
    endTime: (sameDayEnd.isAfter(start) ? sameDayEnd : fallbackEnd).toISOString(),
  };
}

/** Google requires a caller-supplied id that makes the conference request idempotent. */
function buildRequestId() {
  return `polgozar-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function createGoogleMeetEvent({
  accessToken,
  summary,
  description,
  startTime,
  endTime,
  timezone,
}: CreateGoogleMeetInput): Promise<CreateGoogleMeetResult> {
  const response = await fetch(CALENDAR_EVENTS_ENDPOINT, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      summary,
      ...(description ? { description } : {}),
      start: { dateTime: startTime, ...(timezone ? { timeZone: timezone } : {}) },
      end: { dateTime: endTime, ...(timezone ? { timeZone: timezone } : {}) },
      conferenceData: {
        createRequest: {
          requestId: buildRequestId(),
          conferenceSolutionKey: { type: 'hangoutsMeet' },
        },
      },
    }),
  });

  const result = (await response.json().catch(() => null)) as CalendarApiResponse | null;

  if (!response.ok) {
    throw new Error(result?.error?.message || 'Google Calendar rejected the request.');
  }

  const videoEntryPoint = result?.conferenceData?.entryPoints?.find(
    (entryPoint) => entryPoint.entryPointType === 'video',
  );
  const hangoutLink = result?.hangoutLink || videoEntryPoint?.uri;

  if (!hangoutLink) {
    throw new Error('Google Calendar created the event but returned no Meet link.');
  }

  return { hangoutLink, htmlLink: result?.htmlLink, calendarEventId: result?.id };
}

/**
 * Asks for Google authorization and creates the Meet in one step, from the event's own times.
 *
 * The consent popup must be opened while the browser still counts the user's click as recent,
 * so keep any slower work (image resizing, other requests) off the path that leads here.
 */
export async function authorizeAndCreateGoogleMeet({
  summary,
  description,
  eventStartTime,
  eventEndTime,
  timezone,
}: {
  summary: string;
  description?: string;
  eventStartTime?: string | null;
  eventEndTime?: string | null;
  timezone?: string | null;
}): Promise<CreateGoogleMeetResult> {
  const accessToken = await requestGoogleAccessToken(GOOGLE_CALENDAR_EVENTS_SCOPE);
  const { startTime, endTime } = resolveMeetingWindow(eventStartTime, eventEndTime, timezone);

  return createGoogleMeetEvent({
    accessToken,
    summary,
    description,
    startTime,
    endTime,
    timezone: timezone || moment.tz.guess(),
  });
}
